// ═══════════════════════════════════════════════════════════════════════════════
// SECTOR MARKETING LANDING — SOVEREIGN-GRADE INTELLIGENCE FOR EVERY SECTOR
// Every sub-species inherits Anchor AI Guard's world-first autonomous intelligence,
// self-auditing, self-evolving, and self-repairing capabilities.
// Built to the highest professional standard. The world is watching.
// ═══════════════════════════════════════════════════════════════════════════════

import { resolveDefaultLogo } from '@/utils/logoResolver';
import { useState, useEffect, useRef } from 'react';
import { env } from '../../config/env';
import { useAuth } from '../../hooks/useAuth';
import { SECTOR_PLAN_REGISTRY } from '../../backend/src/config/pricing-registry';
import type { SectorMarketingConfig } from './SectorMarketingData';
import { SectorPrivacyPolicy, SectorTermsOfService } from './SectorLegalPages';
import {
  PurchaseTerms,
  CookiePolicy,
  AcceptableUsePolicy,
  DataProcessingAgreement,
  Disclaimer,
  ResponsibleDisclosure,
  ServiceLevelAgreement,
  EndUserLicenceAgreement,
  SovereignDataResidencyPolicy,
  WhistleblowerPolicy,
  IncidentResponsePolicy,
  AccessibilityStatement,
  SecurityPage,
} from '../LegalPages';
import TazzieDevil2Chat from './Heath2Chat';
import SectorScreenshotShowcase from './SectorScreenshotShowcase';

interface SectorMarketingLandingProps {
  config: SectorMarketingConfig;
  onAuthSuccess: () => void;
  onNavigate: (view: string) => void;
  initialAuthMode?: 'landing' | 'login' | 'signup';
  /** Top-nav “Back” target (default: sovereign ecosystem hub, not main marketing). */
  navBackView?: string;
}

const SectorMarketingLanding: React.FC<SectorMarketingLandingProps> = ({
  config,
  onAuthSuccess,
  onNavigate,
  initialAuthMode = 'landing',
  navBackView = 'ecosystem',
}) => {
  const sovereignPasswordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]).{12,100}$/;
  const { login, signup, sectorDemoLogin, demoLogin } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [authMode, setAuthMode] = useState<'landing' | 'login' | 'signup'>(initialAuthMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [legalView, setLegalView] = useState<string | null>(null);
  const [showHowTo, setShowHowTo] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ─── Clear old cached auth data ─────────────────────────────────────
  // This ensures each business operates independently with separate accounts
  const clearOldAuthData = () => {
    // Clear all cached auth data to prevent cross-business session contamination
    localStorage.removeItem('anchor_auth_token');
    localStorage.removeItem('anchor_auth_user');
    localStorage.removeItem('anchor_auth_org');
    localStorage.removeItem('anchor_entitlements');
    localStorage.removeItem('anchor_demo_mode');
    localStorage.removeItem('anchor_demo_sector');
    localStorage.removeItem('anchor_user_sector');
    localStorage.removeItem('anchor_signup_sector');
  };

  // ─── Auth Handlers — wired to real backend ────────────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    if (!email || !password) {
      setAuthError('Email and password are required.');
      return;
    }
    setAuthLoading(true);

    // CRITICAL: Clear old auth data before logging in to a different business
    // This ensures each business operates with its own separate account
    clearOldAuthData();

    try {
      await login(email, password);
      onAuthSuccess();
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    if (!name.trim()) {
      setAuthError('Full name is required.');
      return;
    }
    if (!email || !password) {
      setAuthError('Email and password are required.');
      return;
    }
    if (!sovereignPasswordRegex.test(password)) {
      setAuthError(
        'Password must be 12+ chars and include uppercase, lowercase, number, and special character.'
      );
      return;
    }
    setAuthLoading(true);

    // CRITICAL: Clear old auth data before creating a new business account
    // This ensures each business has its own independent account
    // If user had an account for another business, this starts fresh
    clearOldAuthData();

    try {
      await signup(email, password, name, undefined, config.id);
      onAuthSuccess();
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Signup failed. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  // ─── Demo Access — Investor & Evaluation Mode ────────────────────
  const handleDemoAccess = () => {
    sectorDemoLogin(config.id);
    onAuthSuccess();
  };
  const handleSectorDemo = handleDemoAccess;
  const handleInvestorDemo = () => {
    demoLogin();
    onAuthSuccess();
  };

  // ─── Compliance frameworks per sector ──────────────────────────────
  const getComplianceFrameworks = (): { name: string; icon: string; description: string }[] => {
    const universal = [
      { name: 'ISO 27001', icon: '🔒', description: 'Information Security Management' },
      { name: 'SOC 2 Type II', icon: '🛡️', description: 'Service Organisation Controls' },
      { name: 'GDPR', icon: '🇪🇺', description: 'EU General Data Protection Regulation' },
    ];

    const sectorSpecific: Record<string, { name: string; icon: string; description: string }[]> = {
      medical: [
        { name: 'HIPAA', icon: '🏥', description: 'Health Insurance Portability & Accountability' },
        { name: 'FDA 21 CFR Part 11', icon: '💊', description: 'Electronic Records & Signatures' },
        { name: 'ISO 13485', icon: '🔬', description: 'Medical Device Quality Management' },
        {
          name: 'TGA Compliance',
          icon: '🇦🇺',
          description: 'Australian Therapeutic Goods Administration',
        },
        {
          name: 'GxP / GCP / GLP',
          icon: '📋',
          description: 'Good Practice for Clinical & Laboratory',
        },
        {
          name: 'WH&S (Healthcare)',
          icon: '⚕️',
          description: 'Workplace Health & Safety — Healthcare Settings',
        },
      ],
      finance: [
        {
          name: 'PCI DSS',
          icon: '💳',
          description: 'Payment Card Industry Data Security Standard',
        },
        { name: 'SOX', icon: '📊', description: 'Sarbanes-Oxley Compliance' },
        { name: 'MiFID II', icon: '🇪🇺', description: 'Markets in Financial Instruments Directive' },
        { name: 'Basel III', icon: '🏦', description: 'Banking Capital & Liquidity Standards' },
        {
          name: 'Dodd-Frank',
          icon: '📑',
          description: 'US Financial Reform & Consumer Protection',
        },
        {
          name: 'APRA CPS 234',
          icon: '🇦🇺',
          description: 'Australian Prudential Information Security',
        },
      ],
      legal: [
        {
          name: 'Legal Professional Privilege',
          icon: '⚖️',
          description: 'Client Communication Protection',
        },
        {
          name: 'ABA Model Rules',
          icon: '📜',
          description: 'American Bar Association Ethics Rules',
        },
        { name: 'SRA Standards', icon: '🇬🇧', description: 'Solicitors Regulation Authority (UK)' },
        { name: 'eDiscovery (FRCP)', icon: '💾', description: 'Federal Rules of Civil Procedure' },
        { name: 'CCPA', icon: '🏛️', description: 'California Consumer Privacy Act' },
        {
          name: 'WH&S (Legal)',
          icon: '🛡️',
          description: 'Workplace Health & Safety — Legal Sector',
        },
      ],
      climate: [
        {
          name: 'TCFD',
          icon: '🌡️',
          description: 'Task Force on Climate-Related Financial Disclosures',
        },
        { name: 'CDP', icon: '🌿', description: 'Carbon Disclosure Project' },
        {
          name: 'Paris Agreement',
          icon: '🌍',
          description: 'UN Framework Convention on Climate Change',
        },
        { name: 'EU Taxonomy', icon: '🇪🇺', description: 'EU Sustainable Finance Taxonomy' },
        { name: 'SBTi', icon: '🎯', description: 'Science Based Targets Initiative' },
        { name: 'ISO 14001', icon: '♻️', description: 'Environmental Management Systems' },
      ],
      defence: [
        { name: 'DISP', icon: '🇦🇺', description: 'Defence Industry Security Program' },
        { name: 'ITAR', icon: '🇺🇸', description: 'International Traffic in Arms Regulations' },
        { name: 'NATO STANAG', icon: '🏛️', description: 'NATO Standardisation Agreements' },
        {
          name: 'Essential Eight',
          icon: '8️⃣',
          description: 'Australian Cyber Security Centre Maturity Model',
        },
        {
          name: 'NIST 800-171',
          icon: '📋',
          description: 'Controlled Unclassified Information Protection',
        },
        {
          name: 'Five Eyes Compatible',
          icon: '👁️',
          description: 'Intelligence Alliance Interoperability',
        },
      ],
      energy: [
        {
          name: 'NERC CIP',
          icon: '⚡',
          description: 'North American Electric Reliability Corporation',
        },
        { name: 'IEC 62351', icon: '🔌', description: 'Power Systems Communication Security' },
        { name: 'ISA/IEC 62443', icon: '🏭', description: 'Industrial Automation Security' },
        { name: 'AEMO Compliance', icon: '🇦🇺', description: 'Australian Energy Market Operator' },
        {
          name: 'EU Energy Regulation',
          icon: '🇪🇺',
          description: 'European Energy Market Standards',
        },
        {
          name: 'WH&S (Energy)',
          icon: '⚠️',
          description: 'Workplace Health & Safety — Energy Sector',
        },
      ],
      maritime: [
        {
          name: 'IMO Conventions',
          icon: '🚢',
          description: 'International Maritime Organization Standards',
        },
        {
          name: 'ISPS Code',
          icon: '🔒',
          description: 'International Ship & Port Facility Security',
        },
        { name: 'SOLAS', icon: '⚓', description: 'Safety of Life at Sea Convention' },
        { name: 'MARPOL', icon: '🌊', description: 'Marine Pollution Prevention Convention' },
        { name: 'MLC 2006', icon: '👷', description: 'Maritime Labour Convention' },
        {
          name: 'WH&S (Maritime)',
          icon: '🛟',
          description: 'Workplace Health & Safety — Maritime Operations',
        },
      ],
      space: [
        {
          name: 'Outer Space Treaty',
          icon: '🛰️',
          description: 'UN Treaty on Principles of Outer Space',
        },
        {
          name: 'ITU Radio Regulations',
          icon: '📡',
          description: 'International Telecommunication Union Spectrum',
        },
        {
          name: 'IADC Guidelines',
          icon: '🌌',
          description: 'Inter-Agency Space Debris Coordination',
        },
        {
          name: 'Australian Space Agency',
          icon: '🇦🇺',
          description: 'Australian Space Activities Act Compliance',
        },
        { name: 'ITAR / EAR', icon: '🇺🇸', description: 'US Export Controls for Space Technology' },
        {
          name: 'WH&S (Aerospace)',
          icon: '🧑‍🚀',
          description: 'Workplace Health & Safety — Aerospace Operations',
        },
      ],
      agriculture: [
        { name: 'FAO Standards', icon: '🌾', description: 'UN Food & Agriculture Organization' },
        { name: 'Codex Alimentarius', icon: '📋', description: 'International Food Standards' },
        { name: 'GFSI', icon: '🍽️', description: 'Global Food Safety Initiative' },
        { name: 'USDA Organic', icon: '🇺🇸', description: 'US Department of Agriculture Standards' },
        { name: 'EU CAP', icon: '🇪🇺', description: 'Common Agricultural Policy' },
        {
          name: 'WH&S (Agricultural)',
          icon: '🚜',
          description: 'Workplace Health & Safety — Agricultural Operations',
        },
      ],
      education: [
        { name: 'FERPA', icon: '🎓', description: 'Family Educational Rights & Privacy Act' },
        { name: 'COPPA', icon: '👶', description: "Children's Online Privacy Protection Act" },
        {
          name: 'OECD PISA Aligned',
          icon: '📊',
          description: 'Programme for International Student Assessment',
        },
        { name: 'UNESCO Standards', icon: '🌍', description: 'UN Educational Standards' },
        {
          name: 'Accessibility (WCAG)',
          icon: '♿',
          description: 'Web Content Accessibility Guidelines 2.1 AA',
        },
        {
          name: 'WH&S (Education)',
          icon: '🏫',
          description: 'Workplace Health & Safety — Educational Settings',
        },
      ],
    };

    return [...universal, ...(sectorSpecific[config.id] || [])];
  };

  // ─── Autonomous Intelligence Capabilities (inherited from Anchor) ──
  const autonomousCapabilities = [
    {
      icon: '🧬',
      title: 'Self-Evolution Engine',
      description: `Continuously learns from new ${config.id} data sources and adapts detection models. Runs on a ${config.features.find((f) => f.title.includes('Evolution'))?.description.match(/\d+/)?.[0] || '15'}-second autonomous cycle.`,
      badge: 'WORLD FIRST',
    },
    {
      icon: '🔧',
      title: 'Self-Repair & Auto-Remediation',
      description: `Detects anomalies in ${config.brandName} data pipelines and automatically repairs, reroutes, and restores — zero human intervention required.`,
      badge: 'AUTONOMOUS',
    },
    {
      icon: '📋',
      title: 'Self-Auditing Engine',
      description: `Continuously audits its own outputs for accuracy, bias, and compliance against ${getComplianceFrameworks().length}+ regulatory frameworks. Every decision is logged and explainable.`,
      badge: 'WORLD FIRST',
    },
    {
      icon: '🛡️',
      title: 'Sovereign-Grade Security',
      description: `All ${config.brandName} data is encrypted at rest (AES-256) and in transit (TLS 1.3) with bcrypt-12 authentication, 12-character sovereign password policy, disposable email blocking, and zero-trust architecture throughout. Air-gapped deployment available.`,
      badge: 'SOVEREIGN',
    },
    {
      icon: '🤖',
      title: 'SYMPHONIC Intelligence Core',
      description: `Powered by SYMPHONIC Intelligence — not a single AI model, but a sovereign orchestrator conducting 177 engines in harmony. It surpassed the category of traditional AI. Adapted for ${(config.tagline ?? config.brandName).toLowerCase()} with zero human bottleneck.`,
      badge: 'SYMPHONIC INTELLIGENCE',
    },
    {
      icon: '📡',
      title: 'Real-Time Data Ingestion',
      description: `Connects to ${config.dataSources.length}+ authoritative data sources. Updates continuously with sub-minute latency. Never stale, never behind.`,
      badge: 'REAL-TIME',
    },
  ];

  // ─── FAQ items ─────────────────────────────────────────────────────
  const sectorFaqs = [
    {
      question: `What makes ${config.brandName} different from other ${config.id} intelligence tools?`,
      answer: `${config.brandName} is the world's first self-evolving ${(config.tagline ?? config.brandName).toLowerCase()} platform. Unlike traditional tools that require manual updates and human intervention, our AI continuously learns, adapts, and improves autonomously. Powered by the same SYMPHONIC Intelligence engine that drives Anchor AI Guard — the world's most advanced cybersecurity platform — ${config.brandName} delivers intelligence that other tools simply cannot match.`,
    },
    {
      question: `How does the self-evolution engine work?`,
      answer: `${config.brandName}'s AI Evolution Engine continuously monitors ${config.dataSources.map((d) => d.name).join(', ')}, and dozens more sources. It autonomously generates new detection patterns, improves prediction accuracy, and adapts to emerging trends — all without human intervention. The engine runs on a continuous cycle, ensuring you always have the most current intelligence available.`,
    },
    {
      question: `Is ${config.brandName} compliant with industry regulations?`,
      answer: `Absolutely. ${config.brandName} is built to meet the highest regulatory standards including ${getComplianceFrameworks()
        .slice(0, 5)
        .map((c) => c.name)
        .join(
          ', '
        )}, and more. Our self-auditing engine continuously verifies compliance across all applicable frameworks. Air-gapped and on-premise deployment options are available for the most sensitive environments.`,
    },
    {
      question: `How secure is my data with ${config.brandName}?`,
      answer: `Your data is protected by sovereign-grade security — the same architecture that protects government and defence environments. All data is encrypted at rest (AES-256) and in transit (TLS 1.3). We are SOC 2 Type II compliant and ISO 27001 certified. Data residency options are available for jurisdictions with sovereignty requirements.`,
    },
    {
      question: `Can ${config.brandName} integrate with our existing systems?`,
      answer: `Yes. ${config.brandName} offers a comprehensive REST API with ${config.apiProducts.length} product endpoints, official SDKs for Python, Node.js, Go, Java, C#, and Ruby, and pre-built integrations with major platforms. Our technology partners include ${config.technologyPartners.map((t) => t.name).join(', ')}.`,
    },
    {
      question: `What is the relationship between ${config.brandName} and Anchor AI Guard?`,
      answer: `${config.brandName} is a sovereign business entity within the Anchor Intelligence Federation. It inherits Anchor AI Guard's world-first SYMPHONIC Intelligence engine — the same self-evolving AI that powers 1,300+ security modules and the TAZZIE TV Network streaming platform — adapted specifically for ${(config.tagline ?? config.brandName).toLowerCase()}. Each sector operates as an independent Pty Ltd with its own domain, team, and technology stack.`,
    },
    {
      question: `What Workplace Health & Safety measures are built in?`,
      answer: `${config.brandName} is designed with WH&S compliance at its core. All automated recommendations consider sector-specific health and safety regulations. Our AI flags potential WH&S risks, ensures recommendations align with local legislation, and provides audit trails for regulatory inspection. We meet or exceed WH&S requirements in every jurisdiction we operate.`,
    },
    {
      question: `How do I get started?`,
      answer: `Start with a guided demo of ${config.brandName} — see real workflows in a sandbox. Production access requires a paid subscription; we do not offer automatic 14-day free trials for business accounts. When you subscribe, our onboarding team helps you connect data sources and configure the platform.`,
    },
  ];

  // ─── Sector-Specific How-To Steps ────────────────────────────────
  const getHowToSteps = (): { title: string; description: string; icon: string }[] => {
    const sectorSteps: Record<string, { title: string; description: string; icon: string }[]> = {
      medical: [
        {
          title: 'Connect Health Data Sources',
          description:
            'Link your EHR, patient systems, FDA feeds, and clinical trial databases securely via our pre-built connectors.',
          icon: '🏥',
        },
        {
          title: 'Configure HIPAA & TGA Compliance',
          description:
            'Set your compliance frameworks — our AI automatically enforces HIPAA, TGA, FDA 21 CFR Part 11, and GxP standards.',
          icon: '📋',
        },
        {
          title: 'Activate Medical Intelligence',
          description:
            'Enable drug interaction monitoring, adverse event detection, clinical trial tracking, and real-time medical threat intelligence.',
          icon: '🧬',
        },
        {
          title: 'Review AI-Generated Insights',
          description:
            'Your self-evolving AI begins delivering actionable medical intelligence within minutes. Every insight is explainable and auditable.',
          icon: '📊',
        },
      ],
      finance: [
        {
          title: 'Connect Financial Feeds',
          description:
            'Integrate market data, regulatory filings, transaction monitoring systems, and bank reporting feeds.',
          icon: '💰',
        },
        {
          title: 'Set Risk & Compliance Parameters',
          description:
            'Configure PCI DSS, SOX, Basel III, APRA CPS 234 and anti-money laundering rule engines.',
          icon: '📋',
        },
        {
          title: 'Activate Financial Intelligence',
          description:
            'Enable real-time market anomaly detection, fraud pattern recognition, and cross-asset correlation analysis.',
          icon: '📈',
        },
        {
          title: 'Monitor & Act on Signals',
          description:
            'Receive AI-generated financial intelligence alerts, compliance reports, and predictive risk assessments in real time.',
          icon: '🎯',
        },
      ],
      legal: [
        {
          title: 'Connect Legal Sources',
          description:
            'Link court databases, regulatory feeds, case law repositories, and contract management systems.',
          icon: '⚖️',
        },
        {
          title: 'Set Jurisdiction & Privilege Rules',
          description:
            'Configure legal professional privilege protections, eDiscovery parameters, and jurisdiction-specific compliance.',
          icon: '📜',
        },
        {
          title: 'Activate Legal Intelligence',
          description:
            'Enable case law analysis, regulatory change tracking, contract review AI, and cross-jurisdictional risk monitoring.',
          icon: '🧠',
        },
        {
          title: 'Leverage AI-Powered Legal Insights',
          description:
            'Receive AI-generated legal analysis, precedent matching, risk assessments, and compliance alerts.',
          icon: '🔍',
        },
      ],
      climate: [
        {
          title: 'Connect Environmental Data',
          description:
            'Integrate satellite imagery, carbon monitoring systems, weather APIs, and ESG reporting platforms.',
          icon: '🌍',
        },
        {
          title: 'Configure ESG & Reporting Frameworks',
          description:
            'Set up TCFD, CDP, SBTi, EU Taxonomy, and Paris Agreement alignment tracking.',
          icon: '📊',
        },
        {
          title: 'Activate Climate Intelligence',
          description:
            'Enable real-time carbon tracking, physical risk modelling, transition pathway analysis, and biodiversity monitoring.',
          icon: '🌿',
        },
        {
          title: 'Generate Compliance & ESG Reports',
          description:
            'Automated ESG reports, climate risk disclosures, and regulatory filings generated by self-evolving AI.',
          icon: '📋',
        },
      ],
      defence: [
        {
          title: 'Establish Secure Connectivity',
          description:
            'Deploy air-gapped or secure-enclave data connections to classified and unclassified intelligence feeds.',
          icon: '🛡️',
        },
        {
          title: 'Configure DISP & ITAR Compliance',
          description:
            'Set Defence Industry Security Program, ITAR, NATO STANAG, Essential Eight, and Five Eyes compatibility.',
          icon: '🏛️',
        },
        {
          title: 'Activate Defence Intelligence',
          description:
            'Enable multi-domain threat fusion, adversary prediction, supply chain risk mapping, and cyber-kinetic correlation.',
          icon: '🎯',
        },
        {
          title: 'Operationalise Intelligence Products',
          description:
            'Receive real-time intelligence briefings, threat assessments, and autonomous defence recommendations.',
          icon: '📡',
        },
      ],
      energy: [
        {
          title: 'Connect Energy Infrastructure',
          description:
            'Integrate SCADA systems, grid monitoring, commodity feeds, and energy market data sources.',
          icon: '⚡',
        },
        {
          title: 'Set NERC CIP & AEMO Rules',
          description:
            'Configure critical infrastructure protection standards, IEC 62443, and energy market compliance.',
          icon: '🔌',
        },
        {
          title: 'Activate Energy Intelligence',
          description:
            'Enable grid stability monitoring, supply forecasting, commodity price prediction, and infrastructure risk analysis.',
          icon: '📊',
        },
        {
          title: 'Act on Energy Insights',
          description:
            'Autonomous alerts for grid anomalies, commodity shifts, regulatory changes, and infrastructure threats.',
          icon: '🎯',
        },
      ],
      maritime: [
        {
          title: 'Connect Maritime Systems',
          description:
            'Integrate AIS feeds, port management systems, vessel tracking, cargo manifests, and weather data.',
          icon: '🚢',
        },
        {
          title: 'Configure IMO & ISPS Compliance',
          description:
            'Set up SOLAS, MARPOL, ISPS Code, MLC 2006, and flag-state regulatory compliance.',
          icon: '⚓',
        },
        {
          title: 'Activate Maritime Intelligence',
          description:
            'Enable vessel tracking, port congestion prediction, sanctions screening, and supply chain route optimisation.',
          icon: '🌊',
        },
        {
          title: 'Monitor Global Maritime Operations',
          description:
            'Real-time maritime risk alerts, piracy threat maps, weather routing, and cargo flow intelligence.',
          icon: '🗺️',
        },
      ],
      space: [
        {
          title: 'Connect Space Systems',
          description:
            'Integrate orbital tracking data, telemetry feeds, spectrum monitoring systems, and launch databases.',
          icon: '🛰️',
        },
        {
          title: 'Configure Space Regulatory Compliance',
          description:
            'Set up Outer Space Treaty, ITU Radio Regulations, ITAR/EAR controls, and Australian Space Agency compliance.',
          icon: '📡',
        },
        {
          title: 'Activate Space Intelligence',
          description:
            'Enable orbital conjunction prediction, debris analysis, spectrum interference detection, and launch window optimisation.',
          icon: '🌌',
        },
        {
          title: 'Operationalise Space Domain Awareness',
          description:
            'Autonomous orbital alerts, collision warnings, spectrum conflict reports, and mission planning intelligence.',
          icon: '🚀',
        },
      ],
      agriculture: [
        {
          title: 'Connect Agricultural Data',
          description:
            'Integrate satellite crop imagery, weather stations, soil sensors, market pricing feeds, and supply chain data.',
          icon: '🌾',
        },
        {
          title: 'Configure FAO & Food Safety Standards',
          description:
            'Set up Codex Alimentarius, GFSI, USDA Organic, EU CAP, and biosecurity compliance frameworks.',
          icon: '🍽️',
        },
        {
          title: 'Activate Agricultural Intelligence',
          description:
            'Enable crop health monitoring, pest/disease prediction, yield estimation, and commodity price forecasting.',
          icon: '🧬',
        },
        {
          title: 'Act on Farm-to-Market Intelligence',
          description:
            'AI-generated planting advisories, harvest timing, market entry recommendations, and risk alerts.',
          icon: '📊',
        },
      ],
      education: [
        {
          title: 'Connect Education Systems',
          description:
            'Integrate LMS platforms, student information systems, assessment databases, and labour market feeds.',
          icon: '🎓',
        },
        {
          title: 'Configure FERPA & Privacy Protections',
          description:
            'Set up FERPA, COPPA, GDPR Article 8, parental consent workflows, and data minimisation rules.',
          icon: '🔒',
        },
        {
          title: 'Activate Education Intelligence',
          description:
            'Enable curriculum gap analysis, student outcome prediction, skills mapping, and personalised learning pathways.',
          icon: '🧠',
        },
        {
          title: 'Leverage AI-Powered Education Insights',
          description:
            'Automated intervention recommendations, skills gap reports, workforce alignment analysis, and institutional benchmarking.',
          icon: '📊',
        },
      ],
    };
    return sectorSteps[config.id] || sectorSteps['medical'];
  };

  // ─── Legal Page Rendering ──────────────────────────────────────────
  if (legalView) {
    const handleLegalBack = () => setLegalView(null);
    const handleLegalNavigate = (view: string) => setLegalView(view);

    // Wrap legal pages with sector branded background — Full Brightness
    const LegalWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
      <div className="relative">
        <div
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            backgroundImage: `url(${config.logoUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            opacity: 1,
          }}
        />
        <div className="fixed inset-0 z-0 pointer-events-none bg-linear-to-b from-gray-950/70 via-gray-950/50 to-gray-950/80" />
        <div className="relative z-10">{children}</div>
      </div>
    );

    // Sector-specific legal pages (branded per sector)
    if (legalView === 'privacy') {
      return (
        <LegalWrapper>
          <SectorPrivacyPolicy
            config={config}
            onBack={handleLegalBack}
            onNavigate={handleLegalNavigate}
          />
        </LegalWrapper>
      );
    }
    if (legalView === 'terms') {
      return (
        <LegalWrapper>
          <SectorTermsOfService
            config={config}
            onBack={handleLegalBack}
            onNavigate={handleLegalNavigate}
          />
        </LegalWrapper>
      );
    }

    // Core Anchor legal pages (universal across all sectors)
    const coreLegalPages: Record<string, React.FC<{ onBack: () => void }>> = {
      'purchase-terms': PurchaseTerms,
      'cookie-policy': CookiePolicy,
      'acceptable-use': AcceptableUsePolicy,
      'data-processing': DataProcessingAgreement,
      disclaimer: Disclaimer,
      'responsible-disclosure': ResponsibleDisclosure,
      sla: ServiceLevelAgreement,
      eula: EndUserLicenceAgreement,
      'data-sovereignty': SovereignDataResidencyPolicy,
      whistleblower: WhistleblowerPolicy,
      'incident-response': IncidentResponsePolicy,
      accessibility: AccessibilityStatement,
      security: SecurityPage,
    };

    const LegalComponent = coreLegalPages[legalView];
    if (LegalComponent) {
      return (
        <LegalWrapper>
          <LegalComponent onBack={handleLegalBack} />
        </LegalWrapper>
      );
    }

    // Fallback — unknown legal view, go back to landing
    setLegalView(null);
  }

  // ─── How-To Guide View ─────────────────────────────────────────────
  if (showHowTo) {
    const howToSteps = getHowToSteps();
    return (
      <div className="min-h-screen bg-slate-950 text-white relative">
        {/* Hard-locked sector logo background — Full Brightness, Stretched */}
        <div
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            backgroundImage: `url(${config.logoUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            opacity: 1,
          }}
        />
        <div className="fixed inset-0 z-0 pointer-events-none bg-linear-to-b from-slate-950/70 via-slate-950/50 to-slate-950/80" />
        <div className="relative z-10">
          {/* Header */}
          <div className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} py-16`}>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <button
                onClick={() => setShowHowTo(false)}
                className="text-white/70 hover:text-white text-sm mb-6 flex items-center gap-1 transition-colors"
              >
                &larr; Back to {config.brandName}
              </button>
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-3xl`}
                >
                  {config.icon}
                </div>
                <div>
                  <h1 className="text-3xl font-bold">How-To Guide & Getting Started</h1>
                  <p className="text-white/70 text-sm">
                    {config.brandName} — Step-by-step platform guide
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Quick Start */}
            <section className="mb-16">
              <h2 className={`text-2xl font-bold ${config.textColor} mb-8`}>
                🚀 Quick Start — Get Up & Running in 4 Steps
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {howToSteps.map((step, i) => (
                  <div
                    key={i}
                    className="bg-slate-900/80 border border-slate-700/50 rounded-2xl p-6 hover:border-slate-500 transition-all"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className={`w-10 h-10 bg-linear-to-br ${config.colorFrom}/20 ${config.colorTo}/20 border ${config.borderColor}/30 rounded-xl flex items-center justify-center text-xl`}
                      >
                        {step.icon}
                      </div>
                      <div
                        className={`w-8 h-8 bg-linear-to-br ${config.colorFrom} ${config.colorTo} rounded-full flex items-center justify-center text-sm font-bold text-white`}
                      >
                        {i + 1}
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">{step.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Platform Features Guide */}
            <section className="mb-16">
              <h2 className={`text-2xl font-bold ${config.textColor} mb-8`}>
                📖 Platform Features Guide
              </h2>
              <div className="space-y-4">
                {config.features.map((feature, i) => (
                  <div
                    key={i}
                    className="bg-slate-900/60 border border-slate-700/30 rounded-xl p-5 hover:border-slate-500 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-12 h-12 shrink-0 bg-linear-to-br ${config.colorFrom}/20 ${config.colorTo}/20 border ${config.borderColor}/30 rounded-xl flex items-center justify-center text-xl`}
                      >
                        {feature.icon}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-md font-bold text-white">{feature.title}</h3>
                          {feature.worldFirst && (
                            <span className="text-[9px] px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full font-bold">
                              WORLD FIRST
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Data Sources Guide */}
            <section className="mb-16">
              <h2 className={`text-2xl font-bold ${config.textColor} mb-8`}>
                📡 Connected Data Sources
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {config.dataSources.map((source, i) => (
                  <div
                    key={i}
                    className="bg-slate-900/60 border border-slate-700/30 rounded-xl p-4 hover:border-slate-500 transition-all"
                  >
                    <h4 className="text-sm font-bold text-white mb-1">{source.name}</h4>
                    <p className="text-xs text-gray-500">{source.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* API Getting Started */}
            <section className="mb-16">
              <h2 className={`text-2xl font-bold ${config.textColor} mb-8`}>
                🔌 API Quick Reference
              </h2>
              <div className="bg-slate-900/80 border border-slate-700/50 rounded-2xl p-6">
                <p className="text-gray-400 text-sm mb-6">
                  {config.brandName} offers {config.apiProducts.length} API products for developers.
                  All endpoints require API key authentication and return JSON responses.
                </p>
                <div className="space-y-3">
                  {config.apiProducts.map((api, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-slate-800/60 rounded-xl px-4 py-3 border border-slate-700/30"
                    >
                      <div>
                        <span className="text-sm font-mono text-cyan-400">{api.endpoint}</span>
                        <span className="text-xs text-gray-500 ml-3">{api.name}</span>
                      </div>
                      <span className={`text-xs font-bold ${config.textColor}`}>
                        {api.pricePerCall}/call
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Compliance & Security Guide */}
            <section className="mb-16">
              <h2 className={`text-2xl font-bold ${config.textColor} mb-8`}>
                🛡️ Compliance & Security
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getComplianceFrameworks().map((fw, i) => (
                  <div
                    key={i}
                    className="bg-slate-900/60 border border-slate-700/30 rounded-xl p-4 hover:border-slate-500 transition-all"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">{fw.icon}</span>
                      <h4 className="text-sm font-bold text-white">{fw.name}</h4>
                    </div>
                    <p className="text-xs text-gray-500">{fw.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Support & Contact */}
            <section className="mb-16">
              <h2 className={`text-2xl font-bold ${config.textColor} mb-8`}>💬 Need Help?</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-slate-900/80 border border-slate-700/50 rounded-2xl p-6 text-center">
                  <div className="text-3xl mb-3">🐾</div>
                  <h3 className="text-md font-bold text-white mb-2">TAZZIEDEVIL2.0 AI Assistant</h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Ask our AI chatbot anything about {config.brandName}. Available 24/7. Click the
                    🐾 button in the bottom-right corner.
                  </p>
                </div>
                <div className="bg-slate-900/80 border border-slate-700/50 rounded-2xl p-6 text-center">
                  <div className="text-3xl mb-3">📧</div>
                  <h3 className="text-md font-bold text-white mb-2">Email Support</h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Reach our team directly at support@{config.domain}. Enterprise customers receive
                    priority response within 1 hour.
                  </p>
                </div>
                <div className="bg-slate-900/80 border border-slate-700/50 rounded-2xl p-6 text-center">
                  <div className="text-3xl mb-3">📚</div>
                  <h3 className="text-md font-bold text-white mb-2">Documentation</h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Full API docs, integration guides, and knowledge base available at docs.
                    {config.domain}.
                  </p>
                </div>
              </div>
            </section>

            {/* Legal Links */}
            <section>
              <h2 className={`text-2xl font-bold ${config.textColor} mb-8`}>
                📜 Legal & Compliance Documents
              </h2>
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Privacy Policy', key: 'privacy' },
                  { label: 'Terms of Service', key: 'terms' },
                  { label: 'EULA', key: 'eula' },
                  { label: 'Purchase Terms', key: 'purchase-terms' },
                  { label: 'SLA', key: 'sla' },
                  { label: 'Cookie Policy', key: 'cookie-policy' },
                  { label: 'Acceptable Use', key: 'acceptable-use' },
                  { label: 'Data Processing Agreement', key: 'data-processing' },
                  { label: 'Data Sovereignty', key: 'data-sovereignty' },
                  { label: 'Disclaimer', key: 'disclaimer' },
                  { label: 'Responsible Disclosure', key: 'responsible-disclosure' },
                  { label: 'Incident Response', key: 'incident-response' },
                  { label: 'Whistleblower Policy', key: 'whistleblower' },
                  { label: 'Accessibility', key: 'accessibility' },
                  { label: 'Security', key: 'security' },
                ].map((doc) => (
                  <button
                    key={doc.key}
                    onClick={() => {
                      setShowHowTo(false);
                      setLegalView(doc.key);
                    }}
                    className="bg-slate-900/60 border border-slate-700/30 rounded-xl px-4 py-3 text-left hover:border-slate-500 transition-all group"
                  >
                    <span className="text-sm text-gray-400 group-hover:text-white transition-colors">
                      {doc.label}
                    </span>
                    <span className="text-xs text-gray-600 block mt-0.5">
                      {config.companyStructure.entity}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          </div>

          {/* TAZZIEDEVIL2.0 AI Helper */}
          <TazzieDevil2Chat config={config} isDemo />
        </div>
        {/* End relative z-10 content wrapper */}
      </div>
    );
  }

  // ─── Auth Forms ────────────────────────────────────────────────────
  if (authMode === 'login' || authMode === 'signup') {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center px-4 relative overflow-hidden">
        {/* Hard-locked sector logo background — Full Brightness, Stretched */}
        <div
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            backgroundImage: `url(${config.logoUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            opacity: 1,
          }}
        />
        <div className="fixed inset-0 z-0 pointer-events-none bg-linear-to-b from-gray-950/70 via-gray-950/50 to-gray-950/80" />
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className={`absolute top-1/4 left-1/4 w-96 h-96 bg-linear-to-r ${config.colorFrom} ${config.colorTo} opacity-10 rounded-full blur-3xl`}
          />
          <div
            className={`absolute bottom-1/4 right-1/4 w-96 h-96 bg-linear-to-r ${config.colorTo} ${config.colorFrom} opacity-10 rounded-full blur-3xl`}
          />
        </div>

        <div className="relative w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div
              className={`w-20 h-20 mx-auto bg-linear-to-br ${config.colorFrom} ${config.colorTo} rounded-2xl flex items-center justify-center text-4xl shadow-2xl mb-4`}
            >
              {config.icon}
            </div>
            <h1 className="text-3xl font-bold">{config.brandName}</h1>
            <p className={`${config.textColor} text-sm mt-1`}>{config.tagline}</p>
          </div>

          {/* Auth Card */}
          <div
            className={`bg-gray-900/80 backdrop-blur-xl rounded-2xl p-8 border ${config.borderColor}/30 shadow-2xl`}
          >
            <h2 className="text-xl font-bold mb-6 text-center">
              {authMode === 'login' ? 'Welcome Back' : 'Create Your Account'}
            </h2>

            {/* Sovereign Authentication Badge */}
            <div className="mb-6 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-4 h-4 text-emerald-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-emerald-300 font-medium text-xs">Sovereign Authentication</p>
                  <p className="text-slate-400 text-[10px]">
                    Zero external dependencies • Military-grade
                  </p>
                </div>
              </div>
            </div>

            {authError && (
              <div
                className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm"
                role="alert"
              >
                {authError}
              </div>
            )}
            <form
              ref={formRef}
              onSubmit={authMode === 'login' ? handleLogin : handleSignup}
              className="space-y-4"
              noValidate
            >
              {authMode === 'signup' && (
                <div>
                  <label htmlFor="sector-name" className="sr-only">
                    Full name
                  </label>
                  <input
                    id="sector-name"
                    type="text"
                    placeholder="Full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoComplete="name"
                    aria-label="Full name"
                    className={`w-full px-4 py-3 rounded-xl bg-gray-800 border border-gray-700 focus:border-${config.accentColor}-500 focus:outline-none transition-colors`}
                  />
                </div>
              )}
              <div>
                <label htmlFor="sector-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="sector-email"
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  aria-label="Email address"
                  className={`w-full px-4 py-3 rounded-xl bg-gray-800 border border-gray-700 focus:border-${config.accentColor}-500 focus:outline-none transition-colors`}
                />
              </div>
              <div>
                <label htmlFor="sector-password" className="sr-only">
                  Password
                </label>
                <input
                  id="sector-password"
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                  aria-label="Password"
                  className={`w-full px-4 py-3 rounded-xl bg-gray-800 border border-gray-700 focus:border-${config.accentColor}-500 focus:outline-none transition-colors`}
                />
              </div>
              {/* Forgot Password — only for login mode */}
              {authMode === 'login' && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => {
                      window.history.pushState({}, '', '/forgot-password');
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    }}
                    className={`text-sm ${config.textColor} hover:underline`}
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              <button
                type="submit"
                disabled={authLoading}
                className={`w-full py-3 rounded-xl bg-linear-to-r ${config.colorFrom} ${config.colorTo} font-bold text-white hover:opacity-90 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {authLoading ? 'Processing…' : authMode === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            {/* Demo Access Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700/50"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-gray-900 px-3 text-gray-500">or try instantly</span>
              </div>
            </div>

            {/* Demo Access Button */}
            <button
              onClick={handleDemoAccess}
              className="w-full py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-400/50 hover:text-emerald-300 transition-all font-semibold flex items-center justify-center gap-2"
            >
              <span>🎯</span> Enter Demo Mode — No Account Needed
            </button>

            {/* Toggle */}
            <p className="text-center text-sm text-gray-500 mt-6">
              {authMode === 'login' ? (
                <>
                  Don&apos;t have an account?{' '}
                  <button
                    onClick={() => setAuthMode('signup')}
                    className={`${config.textColor} font-semibold hover:underline`}
                  >
                    Sign up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    onClick={() => setAuthMode('login')}
                    className={`${config.textColor} font-semibold hover:underline`}
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>

            {/* ═══ DEMO ACCESS — Investors & DSTG ═══ */}
            <div className="mt-6 pt-6 border-t border-gray-800">
              <p className="text-center text-gray-500 text-xs font-semibold mb-3">
                Demo Access for Evaluation
              </p>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleSectorDemo}
                  className={`w-full py-3 px-4 rounded-lg bg-linear-to-r ${config.colorFrom} ${config.colorTo} hover:opacity-90 text-white font-semibold transition-all shadow-lg flex items-center justify-center gap-2 text-sm`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  Try {config.brandName} Demo
                </button>
                <button
                  type="button"
                  onClick={handleInvestorDemo}
                  className="w-full py-2.5 px-4 rounded-lg bg-linear-to-r from-amber-500/80 to-orange-500/80 hover:from-amber-400 hover:to-orange-400 text-white font-semibold transition-all shadow-lg flex items-center justify-center gap-2 text-xs"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  Investor Demo — Full Platform
                </button>
              </div>
              <p className="text-center text-gray-600 text-[10px] mt-2">
                Sovereign intelligence feed • Sandboxed environment • Full UI experience
              </p>
            </div>
          </div>

          {/* Back to landing */}
          <button
            onClick={() => setAuthMode('landing')}
            className="w-full mt-4 text-center text-sm text-gray-500 hover:text-white transition-colors"
          >
            ← Back to {config.brandName}
          </button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // MAIN LANDING PAGE — EPIC SOVEREIGN-GRADE SECTOR EXPERIENCE
  // ═══════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-gray-950 text-white overflow-x-hidden relative">
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* HARD-LOCKED SECTOR LOGO BACKGROUND — Full Brightness            */}
      {/* Same architecture as Anchor's main body and SectorDashboard    */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: `url(${config.logoUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          opacity: 1,
        }}
      />
      {/* Dark gradient overlay on top of logo */}
      <div className="fixed inset-0 z-0 pointer-events-none bg-linear-to-b from-gray-950/70 via-gray-950/50 to-gray-950/80" />

      {/* All content above the background */}
      <div className="relative z-10">
        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* NAVIGATION — Glass Morphism                                    */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <nav
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            scrolled
              ? `bg-linear-to-r ${config.colorFrom}/10 ${config.colorTo}/10 backdrop-blur-xl border-b ${config.borderColor}/20 shadow-lg`
              : ''
          }`}
        >
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 xl:px-12">
            <div className="flex items-center justify-between h-20">
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={() => onNavigate(navBackView)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-all text-sm"
                  aria-label="Back to Intelligence Federation hub"
                >
                  <span className="text-base">←</span>
                  <span className="hidden sm:inline text-xs tracking-wide">Anchor</span>
                </button>
                <div className="w-px h-6 bg-white/10 hidden sm:block" />
                <img
                  src={config.logoUrl}
                  alt={config.brandName}
                  className="w-10 h-10 rounded-xl object-contain shadow-lg"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                <span className="text-lg font-bold text-white whitespace-nowrap">
                  {config.brandName}
                </span>
              </div>
              <div className="hidden lg:flex items-center gap-1 xl:gap-1.5">
                <a
                  href="#features"
                  className={`whitespace-nowrap text-[13px] tracking-wide uppercase ${config.textColor} hover:text-white transition-colors px-2.5 py-1.5 rounded-lg hover:bg-white/5`}
                >
                  Features
                </a>
                <a
                  href="#intelligence"
                  className={`whitespace-nowrap text-[13px] tracking-wide uppercase ${config.textColor} hover:text-white transition-colors px-2.5 py-1.5 rounded-lg hover:bg-white/5`}
                >
                  Intelligence
                </a>
                <a
                  href="#compliance"
                  className={`whitespace-nowrap text-[13px] tracking-wide uppercase ${config.textColor} hover:text-white transition-colors px-2.5 py-1.5 rounded-lg hover:bg-white/5`}
                >
                  Compliance
                </a>
                <a
                  href="#pricing"
                  className={`whitespace-nowrap text-[13px] tracking-wide uppercase ${config.textColor} hover:text-white transition-colors px-2.5 py-1.5 rounded-lg hover:bg-white/5`}
                >
                  Pricing
                </a>
                <a
                  href="#faq"
                  className={`whitespace-nowrap text-[13px] tracking-wide uppercase ${config.textColor} hover:text-white transition-colors px-2.5 py-1.5 rounded-lg hover:bg-white/5`}
                >
                  FAQ
                </a>
                <button
                  onClick={() => setShowHowTo(true)}
                  className={`whitespace-nowrap text-[13px] tracking-wide uppercase ${config.textColor} hover:text-white transition-colors px-2.5 py-1.5 rounded-lg hover:bg-white/5`}
                >
                  How-To
                </button>
                <div className="w-px h-5 bg-white/10 mx-1" />
                <button
                  onClick={() => setLegalView('terms')}
                  className={`whitespace-nowrap text-[13px] tracking-wide uppercase ${config.textColor} hover:text-white transition-colors font-semibold px-2.5 py-1.5 rounded-lg hover:bg-white/5`}
                >
                  Legal
                </button>
              </div>
              <div className="flex items-center gap-2.5 shrink-0">
                <button
                  onClick={handleDemoAccess}
                  className="px-3.5 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-400/50 hover:text-emerald-300 transition-all font-medium text-sm whitespace-nowrap"
                >
                  Try Demo
                </button>
                <button
                  onClick={() => setAuthMode('login')}
                  className={`px-3.5 py-2 rounded-xl bg-linear-to-r ${config.colorFrom}/10 ${config.colorTo}/10 backdrop-blur-sm border ${config.borderColor}/30 ${config.textColor} hover:border-white/50 hover:text-white transition-all text-sm whitespace-nowrap`}
                >
                  Log In
                </button>
                <button
                  onClick={() => setAuthMode('signup')}
                  className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} px-4 py-2 rounded-xl font-semibold transition-all shadow-lg hover:opacity-90 border border-white/20 text-sm whitespace-nowrap`}
                >
                  {env.promoActive ? 'Start Promo Trial' : 'Subscribe'}
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* HERO SECTION — Full Impact                                     */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section className="relative pt-32 pb-24 px-4 overflow-hidden">
          {/* Visible Sector Logo Watermark — Full Brightness */}
          <div className="absolute inset-0 opacity-100 pointer-events-none">
            <img
              src={config.logoUrl}
              alt=""
              className="w-full h-full object-contain"
              style={{ opacity: 1 }}
              draggable="false"
            />
          </div>
          {/* Background Gradient & Glow */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className={`absolute top-1/4 left-1/4 w-96 h-96 bg-linear-to-r ${config.colorFrom} ${config.colorTo} opacity-20 rounded-full blur-3xl`}
            />
            <div
              className={`absolute top-1/3 right-1/4 w-96 h-96 bg-linear-to-r ${config.colorTo} ${config.colorFrom} opacity-15 rounded-full blur-3xl`}
            />
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-linear-to-t from-gray-950 to-transparent" />
          </div>

          <div className="max-w-7xl mx-auto relative">
            <div className="text-center max-w-4xl mx-auto">
              {/* Sector Badge with Logo */}
              <div
                className={`inline-flex items-center gap-2 bg-linear-to-r ${config.colorFrom}/10 ${config.colorTo}/10 border ${config.borderColor}/20 rounded-full px-6 py-2 mb-8`}
              >
                <img
                  src={config.logoUrl}
                  alt={config.brandName}
                  className="w-7 h-7 rounded-lg object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                <span className={`text-sm ${config.textColor} font-bold tracking-wider`}>
                  ANCHOR INTELLIGENCE FEDERATION —{' '}
                  {(config.tagline ?? config.brandName).toUpperCase()}
                </span>
              </div>

              {/* Headline */}
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                The World&apos;s First
                <br />
                <span
                  className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} bg-clip-text text-transparent`}
                >
                  Self-Evolving {(config.tagline ?? config.brandName).replace('Autonomous ', '')}{' '}
                  Platform
                </span>
              </h1>

              {/* DREADCORE SI — Small corner accent */}
              <div
                className="absolute top-6 right-6 md:top-8 md:right-10 pointer-events-none select-none z-10"
                aria-hidden="true"
              >
                <div className="relative w-16 h-16 md:w-24 md:h-24">
                  <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-2xl" />
                  <img
                    src={resolveDefaultLogo()}
                    alt=""
                    draggable={false}
                    className="w-full h-full object-contain opacity-25 drop-shadow-[0_0_15px_rgba(168,85,247,0.3)]"
                  />
                </div>
              </div>

              {/* Architecture tagline */}
              <p
                className={`text-3xl md:text-4xl ${config.textColor} mb-4 max-w-4xl mx-auto font-black tracking-wide`}
              >
                Powered by{' '}
                <span className="bg-linear-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                  SYMPHONIC Intelligence
                </span>
              </p>
              <p className="text-xl md:text-2xl text-gray-300 mb-2 max-w-3xl mx-auto font-bold">
                Self-describing. Self-auditing. Self-evolving.
              </p>

              {/* Subtitle */}
              <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">{config.heroSubtitle}</p>

              {/* Problem Statement */}
              <p className="text-lg text-gray-400 mb-4 max-w-2xl mx-auto">
                <strong className={`${config.textColor}`}>
                  {config.problemStatement?.split('.')[0] ?? ''}.
                </strong>{' '}
                {config.solutionStatement}
              </p>

              <p className="text-md text-amber-400 mb-10 max-w-2xl mx-auto font-bold">
                {config.companyStructure.entity} — A sovereign business entity under the Anchor
                Intelligence Federation.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <button
                  onClick={() => setAuthMode('signup')}
                  className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} hover:opacity-90 px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg border border-white/20`}
                >
                  {config.ctaText} →
                </button>
                <button
                  onClick={() => setAuthMode('login')}
                  className="border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all hover:bg-white/5"
                >
                  Log In
                </button>
              </div>

              {/* Demo Access — Instant no-signup entry */}
              <div className="flex justify-center mb-12">
                <button
                  onClick={handleDemoAccess}
                  className="group flex items-center gap-3 px-8 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-400/50 hover:text-emerald-300 transition-all"
                >
                  <span className="text-lg">🎯</span>
                  <span className="font-semibold">Try Full Demo — No Sign-up Required</span>
                  <span className="text-xs bg-emerald-500/20 px-2 py-0.5 rounded-full border border-emerald-500/30 group-hover:bg-emerald-500/30">
                    INSTANT ACCESS
                  </span>
                </button>
              </div>

              {/* CTA Subtext */}
              <p className="text-sm text-gray-500">{config.ctaSubtext}</p>

              {/* Target Markets */}
              <div className="flex flex-wrap justify-center gap-3 mt-6">
                {(config.targetMarket ?? []).map((market) => (
                  <span
                    key={market}
                    className={`text-xs px-3 py-1.5 rounded-full border ${config.borderColor}/20 ${config.textColor}/80 bg-linear-to-r ${config.colorFrom}/5 ${config.colorTo}/5`}
                  >
                    {market}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* STATS SECTION — Sector Metrics                                 */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section className="py-20 px-4 border-y border-gray-800 bg-gray-900/30">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {(config.stats ?? []).map((stat) => (
                <div key={stat.label} className="text-center">
                  <div
                    className={`text-4xl md:text-5xl font-bold bg-linear-to-r ${config.colorFrom} ${config.colorTo} bg-clip-text text-transparent mb-2`}
                  >
                    {stat.value}
                  </div>
                  <div className={`${config.textColor}`}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* SECTOR DASHBOARD SCREENSHOT SHOWCASE — See What's Inside         */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <SectorScreenshotShowcase initialSector={config.id} />

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* INDUSTRY BLIND SPOT CALLOUT — The Uncomfortable Truth           */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div
              className={`bg-linear-to-r ${config.colorFrom}/15 via-pink-900/10 ${config.colorTo}/15 border ${config.borderColor}/30 rounded-3xl p-10 text-center`}
            >
              <div className="text-5xl mb-4">⚠️</div>
              <h3 className="text-3xl font-bold text-white mb-4">
                The Uncomfortable Truth About{' '}
                {config.id.charAt(0).toUpperCase() + config.id.slice(1)} Intelligence Today
              </h3>
              <p className="text-lg text-gray-300 mb-6 max-w-3xl mx-auto">
                {config.problemStatement}{' '}
                <strong className={`${config.textColor}`}>
                  Every existing tool in this space is reactive, fragmented, and manually dependent.
                </strong>
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                {[
                  'Manual Updates\nRequired',
                  'Siloed Data\nSources',
                  'No Self-\nEvolution',
                  'Delayed\nIntelligence',
                  'No Autonomous\nRemediation',
                  'Compliance\nGaps',
                ].map((gap) => (
                  <div key={gap} className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <div className="text-red-400 text-lg mb-1">✗</div>
                    <div className="text-xs text-red-300 whitespace-pre-line">{gap}</div>
                    <div className="text-[10px] text-red-400/60 mt-1">Industry standard</div>
                  </div>
                ))}
              </div>
              <p className={`text-xl font-bold ${config.textColor} mb-2`}>
                {config.brandName} solves every single one. Autonomously.
              </p>
              <p className="text-sm text-gray-500">
                No other {config.id} intelligence platform in the world offers self-evolution,
                self-auditing, or autonomous remediation.
              </p>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* FEATURES SHOWCASE — World-First Intelligence                    */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section
          id="features"
          className={`py-24 px-4 bg-linear-to-b ${config.colorFrom}/5 to-transparent`}
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div
                className={`inline-flex items-center gap-2 bg-linear-to-r ${config.colorFrom}/10 ${config.colorTo}/10 border ${config.borderColor}/20 rounded-full px-6 py-2 mb-6`}
              >
                <span className={`${config.textColor} font-bold text-sm tracking-wider`}>
                  🏆 WORLD-FIRST INTELLIGENCE CAPABILITIES
                </span>
              </div>
              <h2 className="text-4xl md:text-6xl font-bold mb-6">
                <span
                  className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} bg-clip-text text-transparent`}
                >
                  {config.features.filter((f) => f.worldFirst).length} World-First Features
                </span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Capabilities no other {config.id} intelligence platform has ever built. Each one is
                a breakthrough. Together, they redefine the entire category.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {config.features.map((feature, i) => (
                <div
                  key={i}
                  className={`relative bg-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-gray-500 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 group ${
                    feature.worldFirst ? `hover:shadow-${config.accentColor}-500/20` : ''
                  }`}
                >
                  {feature.worldFirst && (
                    <div
                      className={`absolute -top-2.5 left-4 px-3 py-0.5 bg-linear-to-r ${config.colorFrom} ${config.colorTo} rounded-full text-[10px] font-bold text-white tracking-wider`}
                    >
                      WORLD FIRST
                    </div>
                  )}
                  <div
                    className={`w-14 h-14 bg-linear-to-br ${config.colorFrom}/20 ${config.colorTo}/20 border ${config.borderColor}/30 rounded-2xl flex items-center justify-center text-2xl mb-4`}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-300 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* Data Sources */}
            <div
              className={`bg-linear-to-r ${config.colorFrom}/5 ${config.colorTo}/5 border ${config.borderColor}/20 rounded-2xl p-8 text-center`}
            >
              <h3 className="text-xl font-bold text-white mb-4">Authoritative Data Sources</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {config.dataSources.map((source) => (
                  <div
                    key={source.name}
                    className="bg-gray-900/60 border border-gray-700/40 rounded-xl px-4 py-2"
                  >
                    <div className="text-sm font-semibold text-white">{source.name}</div>
                    <div className="text-xs text-gray-500">{source.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* AUTONOMOUS INTELLIGENCE — SYMPHONIC Intelligence Powered                        */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section
          id="intelligence"
          className="py-32 px-4 bg-linear-to-b from-gray-950 via-purple-900/10 to-gray-950 relative overflow-hidden"
        >
          {/* DREADCORE SI background glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-linear-to-r from-purple-500/15 via-cyan-500/10 to-pink-500/15 rounded-full blur-[120px]" />
          </div>

          <div className="max-w-7xl mx-auto relative">
            {/* DREADCORE SI — Small accent, top-right */}
            <div
              className="absolute top-4 right-4 md:top-8 md:right-12 pointer-events-none select-none"
              aria-hidden="true"
            >
              <div className="relative w-14 h-14 md:w-20 md:h-20">
                <div className="absolute inset-0 bg-purple-500/15 rounded-full blur-xl" />
                <img
                  src={resolveDefaultLogo()}
                  alt=""
                  draggable={false}
                  className="w-full h-full object-contain opacity-20 drop-shadow-[0_0_12px_rgba(168,85,247,0.25)]"
                />
              </div>
            </div>

            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-3 bg-linear-to-r from-purple-500/15 to-cyan-500/15 border-2 border-purple-500/30 rounded-full px-8 py-3 mb-8 shadow-[0_0_40px_rgba(168,85,247,0.2)]">
                <span className="text-purple-400 font-black text-lg md:text-xl tracking-[0.2em]">
                  🧠 DREADCORE SI — SYMPHONIC INTELLIGENCE
                </span>
              </div>
              <h2 className="text-5xl md:text-7xl lg:text-8xl font-black mb-8 leading-tight">
                <span className="bg-linear-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                  Autonomous Intelligence
                </span>
                <br />
                <span className="bg-linear-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Core Engine
                </span>
              </h2>
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                Every capability below is inherited from{' '}
                <strong className="text-cyan-400">
                  Anchor AI Guard&apos;s SYMPHONIC Intelligence engine
                </strong>{' '}
                — the same self-evolving intelligence that powers 1,300+ security modules, adapted
                for {(config.tagline ?? config.brandName).toLowerCase()}.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {autonomousCapabilities.map((cap, i) => (
                <div
                  key={i}
                  className="bg-linear-to-br from-purple-500/10 via-gray-900 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20 hover:border-purple-400/50 transition-all hover:shadow-lg hover:shadow-purple-500/10 group"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-linear-to-br from-purple-500/20 to-cyan-500/20 border border-purple-500/30 rounded-xl flex items-center justify-center text-2xl">
                      {cap.icon}
                    </div>
                    <span
                      className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${
                        cap.badge === 'WORLD FIRST'
                          ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                          : cap.badge === 'SYMPHONIC Intelligence POWERED'
                            ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                            : cap.badge === 'SOVEREIGN'
                              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                              : cap.badge === 'AUTONOMOUS'
                                ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30'
                                : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      }`}
                    >
                      {cap.badge}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{cap.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{cap.description}</p>
                </div>
              ))}
            </div>

            {/* DREADCORE SI — Architecture Stats Banner */}
            <div className="bg-linear-to-r from-purple-500/15 via-cyan-500/15 to-pink-500/15 backdrop-blur-sm border-2 border-purple-500/30 rounded-3xl p-12 text-center shadow-[0_0_60px_rgba(168,85,247,0.15)]">
              <h3 className="text-2xl md:text-3xl font-black mb-2">
                <span className="bg-linear-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                  DREADCORE SI
                </span>
              </h3>
              <p className="text-lg text-gray-300 mb-6">
                <strong className="text-white">{config.brandName}</strong> inherits every
                breakthrough from Anchor AI Guard&apos;s SYMPHONIC Intelligence:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { val: '1,200+', label: 'Total Modules' },
                  { val: '46', label: 'Intelligence Engines' },
                  { val: '80+', label: 'World-First Features' },
                  { val: '24/7', label: 'Autonomous Operation' },
                  { val: '17', label: 'Sovereign Capabilities' },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="bg-gray-900/60 rounded-xl p-4 border border-purple-500/20 hover:border-purple-400/40 transition-all"
                  >
                    <div className="text-2xl md:text-3xl font-black text-cyan-400">{s.val}</div>
                    <div className="text-sm text-slate-400 font-medium">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* USE CASES — Industry Applications                              */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section className="py-24 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Built for Real
                <span
                  className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} bg-clip-text text-transparent`}
                >
                  {' '}
                  {config.id.charAt(0).toUpperCase() + config.id.slice(1)} Operations
                </span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Every feature is designed for professionals who need intelligence they can trust and
                act on.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {config.useCases.map((uc, i) => (
                <div
                  key={i}
                  className={`bg-gray-900/60 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 hover:border-gray-500 transition-all hover:shadow-lg group`}
                >
                  <div
                    className={`w-10 h-10 bg-linear-to-br ${config.colorFrom}/20 ${config.colorTo}/20 border ${config.borderColor}/30 rounded-xl flex items-center justify-center text-lg mb-4`}
                  >
                    {['🎯', '🔬', '📊', '🌐'][i % 4]}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors">
                    {uc.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">{uc.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* TECHNOLOGY PARTNERS — Elon Musk Companies + xAI                */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section
          id="partners"
          className="py-24 px-4 bg-linear-to-b from-gray-950 via-purple-900/8 to-gray-950 border-y border-purple-500/10"
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="bg-linear-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                  Technology Partners
                </span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Integrated with the world&apos;s most advanced technology ecosystems.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {config.technologyPartners.map((partner, i) => (
                <div
                  key={i}
                  className="bg-gray-900/60 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-purple-500/30 transition-all hover:shadow-lg group"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 bg-linear-to-br from-purple-500/20 to-cyan-500/20 border border-purple-500/30 rounded-2xl flex items-center justify-center text-2xl">
                      {partner.logo}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white group-hover:text-purple-400 transition-colors">
                        {partner.name}
                      </h3>
                      <p className="text-sm text-gray-500">{partner.description}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-400 leading-relaxed">{partner.integration}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* API PRODUCTS — Developer Ecosystem                             */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section className="py-24 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div
                className={`inline-flex items-center gap-2 bg-linear-to-r ${config.colorFrom}/10 ${config.colorTo}/10 border ${config.borderColor}/20 rounded-full px-6 py-2 mb-6`}
              >
                <span className={`${config.textColor} font-bold text-sm`}>API</span>
                <span className="mx-2 text-gray-600">|</span>
                <span className="text-sm text-gray-300 font-semibold">
                  {config.brandName.toUpperCase()} INTELLIGENCE API
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span
                  className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} bg-clip-text text-transparent`}
                >
                  {config.apiProducts.length} API Products
                </span>
                <span className="text-white"> — One Platform</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Integrate {config.brandName} intelligence directly into your existing systems.
                Official SDKs for Python, Node.js, Go, Java, C#, and Ruby.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {config.apiProducts.map((api, i) => (
                <div
                  key={i}
                  className={`bg-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:${config.borderColor} transition-all hover:shadow-lg`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-white">{api.name}</h3>
                    <span className={`text-sm font-bold ${config.textColor}`}>
                      {api.pricePerCall}/call
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 mb-3">{api.description}</p>
                  <code className="text-xs text-gray-500 bg-gray-800 px-3 py-1.5 rounded-lg">
                    {api.endpoint}
                  </code>
                </div>
              ))}
            </div>

            {/* Code Example */}
            <div className="max-w-2xl mx-auto">
              <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-800 border-b border-gray-700">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-xs text-gray-500 ml-2">Python</span>
                </div>
                <pre className="p-4 text-sm text-gray-300 overflow-x-auto">
                  <code>{`import requests

API_KEY = "anc_${config.id}_your_key_here"
BASE = "https://${config.domain}/api/v1"

# ${config.apiProducts[0]?.name || 'Query Intelligence'}
result = requests.post(f"{BASE}${config.apiProducts[0]?.endpoint || '/query'}",
  headers={"x-api-key": API_KEY},
  json={"query": "latest intelligence"}
).json()

print(result["data"])
# → Instant, AI-powered ${config.id} intelligence`}</code>
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* GLOBAL COMPLIANCE & WH&S — Regulatory Frameworks               */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section
          id="compliance"
          className="py-24 px-4 bg-linear-to-b from-gray-950 via-emerald-900/8 to-gray-950 border-y border-emerald-500/10"
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-6 py-2 mb-6">
                <span className="text-emerald-400 font-bold text-sm tracking-wider">
                  ✅ GLOBAL COMPLIANCE & WORKPLACE HEALTH & SAFETY
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-linear-to-r from-emerald-400 via-green-400 to-cyan-400 bg-clip-text text-transparent">
                  {getComplianceFrameworks().length}+ Regulatory Frameworks
                </span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                {config.brandName} is built to comply with the highest global standards — including
                sector-specific legislation and{' '}
                <strong className="text-emerald-400">Workplace Health & Safety requirements</strong>{' '}
                in every jurisdiction.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-12">
              {getComplianceFrameworks().map((framework, i) => (
                <div
                  key={i}
                  className="bg-gray-900/60 backdrop-blur-sm rounded-xl p-4 border border-emerald-500/10 hover:border-emerald-500/30 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{framework.icon}</span>
                    <div>
                      <h4 className="font-bold text-white group-hover:text-emerald-400 transition-colors">
                        {framework.name}
                      </h4>
                      <p className="text-xs text-gray-500">{framework.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Self-Auditing Compliance Banner */}
            <div className="bg-linear-to-r from-emerald-500/10 via-green-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-2xl p-8 text-center">
              <h3 className="text-xl font-bold text-white mb-3">
                🤖 Autonomous Compliance — Always On, Always Current
              </h3>
              <p className="text-gray-400 max-w-3xl mx-auto mb-4">
                {config.brandName}&apos;s self-auditing engine continuously verifies compliance
                against all applicable regulatory frameworks. When regulations change, the system
                automatically adapts — ensuring you&apos;re never caught off guard.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-900/50 rounded-xl p-3 border border-gray-700/40">
                  <div className="text-xl font-bold text-emerald-400">24/7</div>
                  <div className="text-xs text-gray-500">Continuous Auditing</div>
                </div>
                <div className="bg-gray-900/50 rounded-xl p-3 border border-gray-700/40">
                  <div className="text-xl font-bold text-green-400">Auto</div>
                  <div className="text-xs text-gray-500">Regulatory Adaptation</div>
                </div>
                <div className="bg-gray-900/50 rounded-xl p-3 border border-gray-700/40">
                  <div className="text-xl font-bold text-cyan-400">100%</div>
                  <div className="text-xs text-gray-500">Audit Trail Coverage</div>
                </div>
                <div className="bg-gray-900/50 rounded-xl p-3 border border-gray-700/40">
                  <div className="text-xl font-bold text-amber-400">WH&S</div>
                  <div className="text-xs text-gray-500">Sector-Specific Safety</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* TESTIMONIALS — Social Proof                                    */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section className="py-24 px-4 bg-linear-to-b from-purple-500/5 to-cyan-500/5">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span
                  className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} bg-clip-text text-transparent`}
                >
                  Trusted by Industry Leaders
                </span>
              </h2>
              <p className="text-xl text-gray-400">
                See why professionals choose {config.brandName} for mission-critical intelligence.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {(config.testimonials ?? []).map((t, i) => (
                <div
                  key={i}
                  className={`bg-linear-to-br ${config.colorFrom}/10 ${config.colorTo}/10 backdrop-blur-sm rounded-2xl p-6 border ${config.borderColor}/30 hover:border-purple-500/50 transition-all hover:shadow-lg`}
                >
                  <div className={`text-4xl mb-4 ${config.textColor}`}>&ldquo;</div>
                  <p className="text-gray-300 mb-6 leading-relaxed">{t.quote}</p>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{t.avatar}</span>
                    <div>
                      <div className={`font-semibold ${config.textColor}`}>{t.author}</div>
                      <div className="text-sm text-gray-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* COMPANY STRUCTURE — The Pty Ltd Entity                          */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section className="py-24 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span
                  className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} bg-clip-text text-transparent`}
                >
                  A Sovereign Business Entity
                </span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                {config.brandName} operates as an independent subsidiary within the Anchor
                Intelligence Federation.
              </p>
            </div>

            <div
              className={`bg-gray-900/80 backdrop-blur-sm rounded-3xl p-10 border ${config.borderColor}/20`}
            >
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className={`w-16 h-16 bg-linear-to-br ${config.colorFrom} ${config.colorTo} rounded-2xl flex items-center justify-center text-3xl shadow-lg`}
                    >
                      {config.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        {config.companyStructure.entity}
                      </h3>
                      <p className={`text-sm ${config.textColor}`}>
                        {config.companyStructure.division}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 bg-gray-800/40 rounded-lg px-4 py-2.5 border border-gray-700/40">
                      <span className="text-lg">🏢</span>
                      <span className="text-sm text-gray-300">
                        <strong>HQ:</strong> {config.companyStructure.hq}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 bg-gray-800/40 rounded-lg px-4 py-2.5 border border-gray-700/40">
                      <span className="text-lg">👥</span>
                      <span className="text-sm text-gray-300">
                        <strong>Team:</strong> {config.companyStructure.headcount}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 bg-gray-800/40 rounded-lg px-4 py-2.5 border border-gray-700/40">
                      <span className="text-lg">🌐</span>
                      <span className="text-sm text-gray-300">
                        <strong>Domain:</strong> {config.domain}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white mb-4">
                    Registrations & Certifications
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {config.companyStructure.registrations.map((reg, i) => (
                      <span
                        key={i}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium bg-linear-to-r ${config.colorFrom}/10 ${config.colorTo}/10 border ${config.borderColor}/20 ${config.textColor}`}
                      >
                        ✓ {reg}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* PRICING — Transparent Tiers                                    */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section
          id="pricing"
          className={`py-24 px-4 bg-linear-to-b ${config.colorFrom}/5 to-transparent`}
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span
                  className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} bg-clip-text text-transparent`}
                >
                  Simple, Transparent Pricing
                </span>
              </h2>
              <p className="text-xl text-gray-400">
                Self-evolving intelligence. Self-auditing compliance. Sovereign-grade security.
              </p>
            </div>

            <div className={`grid md:grid-cols-${(config.pricing ?? []).length} gap-6 mb-12`}>
              {(config.pricing ?? []).map((tier, i) => (
                <div
                  key={i}
                  className={`relative bg-gray-900/80 backdrop-blur-sm rounded-2xl p-8 border-2 transition-all hover:shadow-2xl ${
                    tier.highlighted
                      ? `${config.borderColor} shadow-lg`
                      : 'border-gray-700/30 hover:border-gray-500'
                  }`}
                >
                  {tier.highlighted && (
                    <div
                      className={`absolute -top-3 left-1/2 -translate-x-1/2 bg-linear-to-r ${config.colorFrom} ${config.colorTo} px-4 py-1 rounded-full text-xs font-bold text-white`}
                    >
                      MOST POPULAR
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-white mb-2">{tier.name}</h3>
                  <div className="mb-6">
                    <span
                      className={`text-4xl font-bold ${tier.highlighted ? config.textColor : 'text-cyan-400'}`}
                    >
                      {tier.price}
                    </span>
                    <span className="text-gray-500">{tier.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, fi) => (
                      <li key={fi} className="flex items-start gap-2">
                        <span className="text-emerald-400 mt-0.5">✓</span>
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => {
                      // Business plans purchase their exact business SKU. A generic
                      // core platform tier must never be persisted here: paying for
                      // a core plan cannot unlock this business, and access stays
                      // locked until the server confirms bank payment.
                      const selectedSku = Object.values(SECTOR_PLAN_REGISTRY).find(
                        (plan) =>
                          plan.businessId === config.id &&
                          plan.name.endsWith(`— ${tier.name}`)
                      )?.skuId;
                      localStorage.removeItem('anchor_selected_plan');
                      localStorage.removeItem('anchor_selected_sku');
                      if (selectedSku) {
                        localStorage.setItem('anchor_selected_sku', selectedSku);
                      }
                      localStorage.setItem('anchor_signup_sector', config.id);
                      setAuthMode('signup');
                    }}
                    className={`w-full py-3 rounded-xl font-bold transition-all ${
                      tier.highlighted
                        ? `bg-linear-to-r ${config.colorFrom} ${config.colorTo} text-white hover:opacity-90 shadow-lg`
                        : `border ${config.borderColor}/50 ${config.textColor} hover:bg-white/5`
                    }`}
                  >
                    {tier.cta}
                  </button>
                </div>
              ))}
            </div>

            <div className="text-center text-sm text-gray-500">
              All plans include: Self-evolution engine • Self-auditing compliance • Sovereign-grade
              encryption • WH&S compliance • 24/7 autonomous operation • Full API access
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* FAQ SECTION — Comprehensive                                    */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section id="faq" className="py-24 px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span
                  className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} bg-clip-text text-transparent`}
                >
                  Frequently Asked Questions
                </span>
              </h2>
            </div>

            <div className="space-y-4">
              {sectorFaqs.map((faq, index) => (
                <div
                  key={index}
                  className={`bg-linear-to-r ${config.colorFrom}/5 ${config.colorTo}/5 backdrop-blur-sm rounded-xl border ${config.borderColor}/20 overflow-hidden hover:${config.borderColor}/50 transition-all`}
                >
                  <button
                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    className={`w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors`}
                  >
                    <span className={`font-semibold ${config.textColor}`}>{faq.question}</span>
                    <svg
                      className={`w-5 h-5 ${config.textColor} transition-transform shrink-0 ml-4 ${activeFaq === index ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {activeFaq === index && (
                    <div className="px-6 pb-4 text-gray-400 leading-relaxed">{faq.answer}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* ABOUT THE ANCHOR INTELLIGENCE FEDERATION                       */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div
              className={`absolute top-1/4 -left-32 w-96 h-96 bg-linear-to-r ${config.colorFrom} ${config.colorTo} opacity-5 rounded-full blur-3xl`}
            />
            <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-pink-500/5 rounded-full blur-3xl" />
          </div>

          <div className="max-w-5xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <p className="text-sm font-semibold tracking-widest uppercase text-pink-400 mb-3">
                The Pioneer Behind Every Sector
              </p>
              <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-linear-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                The Anchor Intelligence Federation
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                42 sovereign intelligence sectors. One unified vision. Built by a pioneer who
                refused to accept the limits of circumstance.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Vision Card */}
              <div className="bg-linear-to-br from-cyan-500/10 to-purple-500/10 rounded-2xl p-8 border border-cyan-500/20">
                <h3 className="text-2xl font-bold text-white mb-4">
                  A Safer World Through Intelligence
                </h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  {config.brandName} is part of the Anchor Intelligence Federation — a world-first
                  collection of 42 sovereign intelligence platforms spanning medical, finance,
                  legal, climate, defence, energy, maritime, space, agriculture, education, law
                  enforcement, and wildlife conservation.
                </p>
                <p className="text-gray-400 leading-relaxed mb-4">
                  Each sector operates as an independent Pty Ltd subsidiary with its own domain,
                  team, pricing, and API products. But every sector inherits the same
                  ground-breaking SYMPHONIC Intelligence — not just an engine, but a sovereign
                  orchestrator that conducts 177 autonomous engines in harmony. It outgrew the
                  category of AI entirely. It powers Anchor AI Guard&apos;s 1,300+ modules and 80+
                  world-first features.
                </p>
                <p className="text-cyan-400 font-semibold">
                  This is not a product suite. This is a new category of intelligence
                  infrastructure.
                </p>
              </div>

              {/* Platform Card */}
              <div className="bg-linear-to-br from-pink-500/10 to-purple-500/10 rounded-2xl p-8 border border-pink-500/20">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-linear-to-br from-cyan-500/20 to-pink-500/20 border-2 border-cyan-500/30 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-cyan-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Anchor AI Guard</h3>
                    <p className="text-pink-400 text-sm">Sovereign Intelligence Platform</p>
                  </div>
                </div>
                <blockquote className="border-l-2 border-pink-500/50 pl-4 mb-4">
                  <p className="text-gray-300 italic leading-relaxed">
                    &ldquo;No starting point is too low, and no vision is too big, when the will to
                    rise is stronger than the circumstances trying to hold you down.&rdquo;
                  </p>
                </blockquote>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Built from the ground up as a sovereign-grade intelligence platform. 42 sector
                  businesses. 177 evolution engines. Protecting organisations, communities, and
                  nations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* HOW-TO & GETTING STARTED — Help & Guides                       */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section className="py-24 px-4 bg-linear-to-b from-gray-950 via-emerald-900/10 to-gray-950">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-linear-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-full px-6 py-2 mb-6">
                <span className="text-emerald-400 font-bold text-sm tracking-wider">
                  📖 HOW-TO & GETTING STARTED
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-linear-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                  Up & Running in Minutes
                </span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                {config.brandName} is designed for instant deployment. Follow these 4 steps to start
                receiving autonomous {config.id} intelligence — or explore the full guide.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {getHowToSteps().map((step, i) => (
                <div
                  key={i}
                  className="relative bg-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-emerald-500/20 hover:border-emerald-400/50 transition-all hover:shadow-lg hover:shadow-emerald-500/10 group"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-linear-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-xl flex items-center justify-center text-xl">
                      {step.icon}
                    </div>
                    <div className="w-8 h-8 bg-linear-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center text-sm font-bold text-white">
                      {i + 1}
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-emerald-300 transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowHowTo(true)}
                className="bg-linear-to-r from-emerald-500 to-cyan-500 hover:opacity-90 px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg border border-white/20"
              >
                📖 Full How-To Guide & Documentation
              </button>
              <button
                onClick={() => setAuthMode('signup')}
                className="border border-emerald-500/50 hover:border-white text-emerald-400 hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all hover:bg-white/5"
              >
                {env.promoActive ? '🚀 Start Promo Trial' : '🚀 Subscribe'}
              </button>
            </div>

            {/* AI Helper Callout */}
            <div className="mt-12 bg-linear-to-r from-purple-500/10 via-pink-500/10 to-cyan-500/10 border border-purple-500/20 rounded-2xl p-8 text-center">
              <div className="text-3xl mb-3">🐾</div>
              <h3 className="text-xl font-bold text-white mb-2">Need Help? Ask TAZZIEDEVIL2.0</h3>
              <p className="text-gray-400 text-sm max-w-2xl mx-auto mb-4">
                Our AI-powered assistant lives in the bottom-right corner of every page. Ask it
                anything about {config.brandName} — features, pricing, compliance, data sources,
                integrations, or troubleshooting. Available 24/7, powered by the same SYMPHONIC
                Intelligence engine.
              </p>
              <p className="text-xs text-gray-600">
                TAZZIEDEVIL2.0 — Tenacious Autonomous Zero-Zone Intelligence Engine. Sector-aware.
                Context-intelligent. Always learning.
              </p>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* FINAL CTA SECTION — Closing Impact                             */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <section className="py-24 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div
              className={`bg-linear-to-r ${config.colorFrom}/10 ${config.colorTo}/10 backdrop-blur-sm border ${config.borderColor}/30 rounded-3xl p-12 transition-all hover:border-purple-500/50`}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span
                  className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} bg-clip-text text-transparent`}
                >
                  {config.ctaText}
                </span>
              </h2>
              <p className="text-xl text-gray-300 mb-4">
                <strong>
                  The world&apos;s first self-evolving{' '}
                  {(config.tagline ?? config.brandName).toLowerCase()} platform.
                </strong>
              </p>
              <p className="text-lg text-gray-400 mb-8">
                Self-auditing. Self-repairing. Autonomous. Sovereign-grade. Starting from{' '}
                <span className={`${config.textColor} font-bold`}>
                  {config.pricing?.[0]?.price ?? 'Contact us'}/month
                </span>
                .
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
                <button
                  onClick={() => setAuthMode('signup')}
                  className={`bg-linear-to-r ${config.colorFrom} ${config.colorTo} hover:opacity-90 px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg border border-white/20`}
                >
                  {env.promoActive ? 'Start Free 14-Day Promo Trial →' : 'Get paid access →'}
                </button>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
                <button
                  onClick={handleSectorDemo}
                  className="bg-linear-to-r from-purple-600/70 to-cyan-600/70 hover:from-purple-500 hover:to-cyan-500 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all border border-purple-500/30 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  Try Demo
                </button>
                <button
                  onClick={handleInvestorDemo}
                  className="bg-linear-to-r from-amber-500/60 to-orange-500/60 hover:from-amber-400 hover:to-orange-400 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all border border-amber-500/30 flex items-center gap-2"
                >
                  Investor Demo
                </button>
              </div>
              <p className="text-sm text-gray-500">{config.ctaSubtext}</p>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* FOOTER — Professional & Comprehensive                          */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <footer
          className={`border-t ${config.borderColor}/20 py-12 px-4 bg-linear-to-r ${config.colorFrom}/5 ${config.colorTo}/5 backdrop-blur-sm`}
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className={`w-10 h-10 bg-linear-to-br ${config.colorFrom} ${config.colorTo} rounded-xl flex items-center justify-center text-xl shadow-lg`}
                  >
                    {config.icon}
                  </div>
                  <span className="font-bold text-white">{config.brandName}</span>
                </div>
                <p className="text-gray-500 text-sm mb-3">
                  {config.tagline}. A sovereign intelligence entity within the Anchor Intelligence
                  Federation.
                </p>
                <p className="text-gray-600 text-xs">{config.companyStructure.entity}</p>
              </div>
              <div>
                <h4 className={`font-semibold ${config.textColor} mb-4`}>Platform</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li>
                    <a href="#features" className="hover:text-white transition-colors">
                      Features
                    </a>
                  </li>
                  <li>
                    <a href="#intelligence" className="hover:text-white transition-colors">
                      Intelligence Core
                    </a>
                  </li>
                  <li>
                    <a href="#compliance" className="hover:text-white transition-colors">
                      Compliance & WH&S
                    </a>
                  </li>
                  <li>
                    <a href="#pricing" className="hover:text-white transition-colors">
                      Pricing
                    </a>
                  </li>
                  <li>
                    <a href="#partners" className="hover:text-white transition-colors">
                      Technology Partners
                    </a>
                  </li>
                  <li>
                    <a href="#faq" className="hover:text-white transition-colors">
                      FAQ
                    </a>
                  </li>
                  <li>
                    <button
                      onClick={() => setShowHowTo(true)}
                      className="hover:text-white transition-colors"
                    >
                      📖 How-To Guide
                    </button>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className={`font-semibold ${config.textColor} mb-4`}>Anchor Federation</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li>
                    <button
                      onClick={() => onNavigate(navBackView)}
                      className="hover:text-white transition-colors"
                    >
                      🛡️ Anchor AI Guard
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => onNavigate('anchor-medical')}
                      className="hover:text-white transition-colors"
                    >
                      🏥 Anchor Medical
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => onNavigate('anchor-finance')}
                      className="hover:text-white transition-colors"
                    >
                      💰 Anchor Finance
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => onNavigate('anchor-legal')}
                      className="hover:text-white transition-colors"
                    >
                      ⚖️ Anchor Legal
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => onNavigate('anchor-climate')}
                      className="hover:text-white transition-colors"
                    >
                      🌍 Anchor Climate
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => onNavigate('anchor-defence')}
                      className="hover:text-white transition-colors"
                    >
                      🛡️ Anchor Defence
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => onNavigate('anchor-energy')}
                      className="hover:text-white transition-colors"
                    >
                      ⚡ Anchor Energy
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => onNavigate('anchor-maritime')}
                      className="hover:text-white transition-colors"
                    >
                      🚢 Anchor Maritime
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => onNavigate('anchor-space')}
                      className="hover:text-white transition-colors"
                    >
                      🛰️ Anchor Space
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => onNavigate('anchor-agriculture')}
                      className="hover:text-white transition-colors"
                    >
                      🌾 Anchor Agriculture
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => onNavigate('anchor-education')}
                      className="hover:text-white transition-colors"
                    >
                      🎓 Anchor Education
                    </button>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className={`font-semibold ${config.textColor} mb-4`}>Legal & Compliance</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li>
                    <button
                      onClick={() => setLegalView('privacy')}
                      className="hover:text-white transition-colors"
                    >
                      Privacy Policy
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('terms')}
                      className="hover:text-white transition-colors"
                    >
                      Terms of Service
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('eula')}
                      className="hover:text-white transition-colors"
                    >
                      End User Licence Agreement
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('purchase-terms')}
                      className="hover:text-white transition-colors"
                    >
                      Purchase Terms
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('sla')}
                      className="hover:text-white transition-colors"
                    >
                      Service Level Agreement
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('data-processing')}
                      className="hover:text-white transition-colors"
                    >
                      Data Processing Agreement
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('data-sovereignty')}
                      className="hover:text-white transition-colors"
                    >
                      Data Sovereignty Policy
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('cookie-policy')}
                      className="hover:text-white transition-colors"
                    >
                      Cookie Policy
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('acceptable-use')}
                      className="hover:text-white transition-colors"
                    >
                      Acceptable Use Policy
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('disclaimer')}
                      className="hover:text-white transition-colors"
                    >
                      Disclaimer
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('responsible-disclosure')}
                      className="hover:text-white transition-colors"
                    >
                      Responsible Disclosure
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('incident-response')}
                      className="hover:text-white transition-colors"
                    >
                      Incident Response Policy
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('whistleblower')}
                      className="hover:text-white transition-colors"
                    >
                      Whistleblower Policy
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('accessibility')}
                      className="hover:text-white transition-colors"
                    >
                      Accessibility Statement
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setLegalView('security')}
                      className="hover:text-white transition-colors"
                    >
                      Security
                    </button>
                  </li>
                  <li>
                    <a
                      href="mailto:soc2@anchoraiguard.com"
                      className="hover:text-white transition-colors"
                    >
                      SOC 2 Audit Report
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Disclaimer */}
            {config.disclaimer && (
              <div className="border-t border-gray-800 pt-6 mb-6">
                <p className="text-xs text-gray-600 text-center max-w-3xl mx-auto">
                  ⚠️ {config.disclaimer}
                </p>
              </div>
            )}

            {/* ── DREADCORE SI Robot — Locked to footer ─────────────────── */}
            <div
              className="relative flex items-center justify-center py-8 pointer-events-none select-none"
              aria-hidden="true"
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className={`w-48 h-48 md:w-64 md:h-64 ${config.colorFrom}/10 rounded-full blur-3xl`}
                />
              </div>
              <img
                src={resolveDefaultLogo()}
                alt=""
                draggable={false}
                className="relative w-28 h-28 md:w-40 md:h-40 object-contain opacity-25 drop-shadow-[0_0_40px_rgba(34,211,238,0.4)]"
              />
            </div>

            <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={resolveDefaultLogo()}
                  alt="DREADCORE SI"
                  draggable={false}
                  className="w-8 h-8 object-contain opacity-50 drop-shadow-[0_0_10px_rgba(34,211,238,0.3)]"
                />
                <p className="text-gray-600 text-sm">
                  © 2026 {config.companyStructure.entity}. All rights reserved. Part of the Anchor
                  Intelligence Federation.
                </p>
              </div>
              <div className="flex items-center gap-6">
                <a
                  href="https://youtube.com/@anchoraiguard"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${config.textColor} hover:text-white transition-colors`}
                >
                  YouTube
                </a>
                <a
                  href="https://linkedin.com/company/anchoraiguard"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${config.textColor} hover:text-white transition-colors`}
                >
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </footer>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* TAZZIEDEVIL2.0 — Floating AI Helper (every page)               */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <TazzieDevil2Chat config={config} isDemo />
      </div>
      {/* End relative z-10 content wrapper */}
    </div>
  );
};

export default SectorMarketingLanding;
